import {
  FlexBox,
  FlexBoxDirection,
  Input,
  Label,
  TextArea,
  WrappingType
} from '@ui5/webcomponents-react';
import { SaveResult, SpecificQueryType } from 'components/shared/interfaces/SpecificQueryTypes';
import dayjs from 'dayjs';
import { IssueType, useCreateNonAbapMutation } from 'generated/graphql';
import isMatch from 'lodash/isMatch';
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useBaseQueryStore } from 'stores/CreateBaseQueryStore';
import { useNonAbapQueryStore } from 'stores/CreateNonAbapQueryStore';
import { CommonNonAbapQueryInitialStateFields } from 'stores/slices/NonAbapQuerySlices/CommonNonAbapQuerySlice';
import { validateString } from 'utils/validator';
import { useStyles } from '../CreateQueryView/CreateQueryView.jss';
import { useCreateQueryFreezeStore } from 'stores/CreateQueryFreezeStore';

export const NonAbapQuery = forwardRef<SpecificQueryType>((_, ref) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const store = useNonAbapQueryStore();
  const freezeStore = useCreateQueryFreezeStore();
  const baseQueryStore = useBaseQueryStore();
  const {
    deadline,
    details,
    projectManager,
    selectedIssueType,
    selectedProject,
    sourceText,
    targetLanguage,
    targetText,
    attachments
  } = baseQueryStore;

  /** GraphQL Queries/Mutations */
  const [{ fetching }, callCreateQuery] = useCreateNonAbapMutation();

  useImperativeHandle(ref, () => ({
    save: () => {
      return onSave();
    },
    cancel: () => {
      store.resetData();
    },
    isValid: () => {
      return validateAllFields();
    },
    containsRelevantChanges: () => {
      return containsRelevantChanges();
    }
  }));

  useEffect(() => {
    store.setFetching(fetching);

    return () => store.setFetching(false);
  }, [fetching]);

  const validateXtmProjectName = () => {
    return validateString(store.xtmProjectName, store.setXtmProjectNameValueState);
  };

  const validateRepository = () => {
    return validateString(store.repository, store.setRepositoryValueState);
  };

  const validateCollection = () => {
    return validateString(store.collection, store.setCollectionValueState);
  };

  const validateFileName = () => {
    return validateString(store.fileName, store.setFileNameValueState);
  };

  const validateStringId = () => {
    return validateString(store.stringId, store.setStringIdValueState);
  };

  const validateSourceContact = () => {
    return validateString(store.sourceContact, store.setSourceContactValueState);
  };

  const validateAllFields = () => {
    const isXtmProjectNameValid = validateXtmProjectName();
    const isRepositoryValid = validateRepository();
    const isCollectionValid = validateCollection();
    const isFileNameValid = validateFileName();
    const isStringIdValid = validateStringId();
    const isSourceContactValid = validateSourceContact();

    return (
      isXtmProjectNameValid &&
      isRepositoryValid &&
      isCollectionValid &&
      isFileNameValid &&
      isStringIdValid &&
      isSourceContactValid
    );
  };

  const onSave = async (): Promise<SaveResult> => {
    const result = await callCreateQuery({
      input: {
        deadline: dayjs(deadline).format('YYYY-MM-DD'),
        issueType: selectedIssueType as IssueType,
        project: selectedProject,
        sourceText: sourceText,
        targetLanguage: targetLanguage,
        details: details,
        pmName: projectManager,
        targetText: targetText,
        collection: store.collection,
        fileName: store.fileName,
        repository: store.repository,
        xtmProjectName: store.xtmProjectName,
        stringId: store.stringId,
        sourceContact: store.sourceContact,
        languagesTranslatorTeams: store.languagesTranslatorTeams
      },
      files: attachments
    });
    const success = !result.error;
    if (success) {
      store.resetData();
      return { id: result?.data?.query.id };
    }
    return { error: result.error };
  };

  const containsRelevantChanges = () => {
    return !isMatch(store, CommonNonAbapQueryInitialStateFields);
  };

  const onChangeXtmProjectName = (event: CustomEvent) => {
    const { value } = event.target as HTMLInputElement;
    store.setXtmProjectName(value);
    validateString(value, store.setXtmProjectNameValueState);
  };

  const onChangeRepository = (event: CustomEvent) => {
    const { value } = event.target as HTMLInputElement;
    store.setRepository(value);
    validateString(value, store.setRepositoryValueState);
  };

  const onChangeCollection = (event: CustomEvent) => {
    const { value } = event.target as HTMLInputElement;
    store.setCollection(value);
    validateString(value, store.setCollectionValueState);
  };

  const onChangeFileName = (event: CustomEvent) => {
    const { value } = event.target as HTMLInputElement;
    store.setFileName(value);
    validateString(value, store.setFileNameValueState);
  };

  const onChangeStringId = (event: CustomEvent) => {
    const { value } = event.target as HTMLTextAreaElement;
    store.setStringId(value);
    validateString(value, store.setStringIdValueState);
  };

  const onChangeSourceContact = (event: CustomEvent) => {
    const { value } = event.target as HTMLInputElement;
    store.setSourceContact(value);
    validateString(value, store.setSourceContactValueState);
  };

  return (
    <>
      <FlexBox direction={FlexBoxDirection.Column}>
        <div className={classes.div}>
          <Label id="xtm-project-name-label" className={classes.label} showColon required>
            <span
              data-help-id="create-query-non-abap-xtm-project-name"
              data-testid="xtm-project-name-label"
              title={t('create-query-non-abap-xtm-project-name-tooltip')}
            >
              {t('create-query-non-abap-xtm-project-name')}
            </span>
          </Label>
          <Input
            aria-labelledby="xtm-project-name-label"
            valueState={store.xtmProjectNameValueState}
            valueStateMessage={
              <Label wrappingType={WrappingType.Normal}>
                {t('display-value-state-message-xtm-project-name')}
              </Label>
            }
            onChange={onChangeXtmProjectName}
            className={classes.formLineInput}
            value={store.xtmProjectName}
            disabled={freezeStore.xtmProjectName}
          />
        </div>
      </FlexBox>
      <FlexBox direction={FlexBoxDirection.Column}>
        <div className={classes.div}>
          <Label id="repository-label" className={classes.label} showColon required>
            <span data-help-id="create-query-non-abap-repository">
              {t('create-query-non-abap-repository')}
            </span>
          </Label>
          <Input
            aria-labelledby="repository-label"
            valueState={store.repositoryValueState}
            valueStateMessage={
              <Label wrappingType={WrappingType.Normal}>
                {t('display-value-state-message-repository')}
              </Label>
            }
            onChange={onChangeRepository}
            className={classes.formInput}
            value={store.repository}
            disabled={freezeStore.repository}
          />
          <Label id="collection-label" className={classes.secondColumnLabel} showColon required>
            <span data-help-id="create-query-non-abap-collection">
              {t('create-query-non-abap-collection')}
            </span>
          </Label>
          <Input
            aria-labelledby="collection-label"
            valueState={store.collectionValueState}
            valueStateMessage={
              <Label wrappingType={WrappingType.Normal}>
                {t('display-value-state-message-collection')}
              </Label>
            }
            onChange={onChangeCollection}
            className={classes.formInput}
            value={store.collection}
            disabled={freezeStore.collection}
          />
        </div>
      </FlexBox>
      <FlexBox direction={FlexBoxDirection.Column}>
        <div className={classes.div}>
          <Label id="file-name-label" className={classes.label} showColon required>
            <span data-help-id="create-query-non-abap-file-name">
              {t('create-query-non-abap-file-name')}
            </span>
          </Label>
          <Input
            aria-labelledby="file-name-label"
            valueState={store.fileNameValueState}
            valueStateMessage={
              <Label wrappingType={WrappingType.Normal}>
                {t('display-value-state-message-file-name')}
              </Label>
            }
            onChange={onChangeFileName}
            className={classes.formLineInput}
            value={store.fileName}
            disabled={freezeStore.fileName}
          />
        </div>
      </FlexBox>
      <FlexBox direction={FlexBoxDirection.Column}>
        <div className={classes.div}>
          <Label id="string-id-label" className={classes.label} showColon required>
            <span data-help-id="create-query-non-abap-string-id">
              {t('create-query-non-abap-string-id')}
            </span>
          </Label>
          <TextArea
            aria-labelledby="string-id-label"
            valueState={store.stringIdValueState}
            valueStateMessage={
              <Label wrappingType={WrappingType.Normal}>
                {t('display-value-state-message-string-id')}
              </Label>
            }
            onChange={onChangeStringId}
            value={store.stringId}
            disabled={freezeStore.stringId}
            rows={4}
          />
        </div>
      </FlexBox>
      <FlexBox direction={FlexBoxDirection.Column}>
        <div className={classes.div}>
          <Label id="source-contact-label" className={classes.label} showColon required>
            <span data-help-id="create-query-non-abap-source-contact">
              {t('create-query-non-abap-source-contact')}
            </span>
          </Label>
          <Input
            aria-labelledby="source-contact-label"
            className={classes.formLineInput}
            onChange={onChangeSourceContact}
            valueStateMessage={
              <Label wrappingType={WrappingType.Normal}>
                {t('display-value-state-message-source-contact')}
              </Label>
            }
            valueState={store.sourceContactValueState}
            value={store.sourceContact}
            disabled={freezeStore.sourceContact}
          />
        </div>
      </FlexBox>
    </>
  );
});
